const dayjs = require("dayjs");
var utc = require("dayjs/plugin/utc");
var timezone = require("dayjs/plugin/timezone");
var isoWeek = require("dayjs/plugin/isoWeek");

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

export default ({ initialCurrentDate, userLocale = "en" }) => {
  // Configure Day.js locale based on user preference
  const locale = userLocale === "tr" ? "tr" : "en";
  dayjs.locale(locale);

  return {
    inProgress: false,
    currentDate: initialCurrentDate ? dayjs(initialCurrentDate) : dayjs(),
    startTime: "09:00",
    endTime: "10:00",
    locale: locale,

    // Get localized day names for calendar header
    getLocalizedDayNames() {
      const dayNames = [];
      // Start with Monday (ISO week)
      for (let i = 1; i <= 7; i++) {
        const day = dayjs().isoWeekday(i);
        dayNames.push(day.format("dd")); // Short day names like "Mo", "Tu", etc.
      }
      return dayNames;
    },

    processEvents(events) {
      // Sort events by start time
      const sortedEvents = [...events].sort((a, b) => {
        const startA = this.convertToLocalTime(
          a.when.start_time,
          a.when.start_timezone,
        );
        const startB = this.convertToLocalTime(
          b.when.start_time,
          b.when.start_timezone,
        );
        return startA - startB;
      });

      // Track overlapping groups
      const groups = [];
      let currentGroup = [];

      for (const event of sortedEvents) {
        const eventStart = this.convertToLocalTime(
          event.when.start_time,
          event.when.start_timezone,
        );
        const eventEnd = this.convertToLocalTime(
          event.when.end_time,
          event.when.end_timezone,
        );

        // If currentGroup is empty or event overlaps with last event in group
        if (currentGroup.length === 0) {
          currentGroup.push({ event, column: 0 });
        } else {
          const lastEvent = currentGroup[currentGroup.length - 1].event;
          const lastEnd = this.convertToLocalTime(
            lastEvent.when.end_time,
            lastEvent.when.end_timezone,
          );

          if (eventStart < lastEnd) {
            // Find first available column
            let column = 0;
            while (
              currentGroup.some(
                (item) =>
                  item.column === column &&
                  this.eventsOverlap(item.event, event),
              )
            ) {
              column++;
            }
            currentGroup.push({ event, column });
          } else {
            // No overlap, start new group
            if (currentGroup.length > 0) {
              groups.push(currentGroup);
            }
            currentGroup = [{ event, column: 0 }];
          }
        }
      }

      if (currentGroup.length > 0) {
        groups.push(currentGroup);
      }

      return groups;
    },

    eventsOverlap(event1, event2) {
      const start1 = this.convertToLocalTime(
        event1.when.start_time,
        event1.when.start_timezone,
      );
      const end1 = this.convertToLocalTime(
        event1.when.end_time,
        event1.when.end_timezone,
      );
      const start2 = this.convertToLocalTime(
        event2.when.start_time,
        event2.when.start_timezone,
      );
      const end2 = this.convertToLocalTime(
        event2.when.end_time,
        event2.when.end_timezone,
      );

      return start1 < end2 && start2 < end1;
    },

    getEventStyles(event, events) {
      const groups = this.processEvents(events);
      let eventGroup;
      let groupIndex;

      // Find which group contains this event
      for (let i = 0; i < groups.length; i++) {
        const group = groups[i];
        const foundEvent = group.find((item) => item.event.id === event.id);
        if (foundEvent) {
          eventGroup = group;
          groupIndex = i;
          break;
        }
      }

      if (!eventGroup) {
        return {};
      }

      const maxColumns = Math.max(...eventGroup.map((item) => item.column)) + 1;
      const eventData = eventGroup.find((item) => item.event.id === event.id);
      const column = eventData.column;

      return {
        "grid-row": `${this.getGridRow(event)} / span ${this.getGridSpan(event)}`,
        "grid-column": `${column + 1} / span 1`,
        "margin-right": "2px",
        "z-index": column + 1,
      };
    },

    scrollToBusinessHours() {
      // Wait for the next tick to ensure DOM is updated
      this.$nextTick(() => {
        const container = this.$refs.eventContainer;
        if (container) {
          // For 9 AM:
          // - Skip header row (1.25rem)
          // - Each hour is 12 slots (5-minute intervals)
          // - We want to scroll to 9 * 12 = 108 slots
          const slotHeight = (container.scrollHeight - 1.25) / 288; // Total height minus header divided by number of slots
          const scrollPosition = 9 * 12 * slotHeight;
          container.scrollTop = scrollPosition;
        }
      });
    },
    today() {
      return dayjs();
    },
    setDate(date) {
      this.currentDate = dayjs(date);
    },
    nextMonth() {
      this.currentDate = this.currentDate.add(1, "month");
    },
    previousMonth() {
      this.currentDate = this.currentDate.subtract(1, "month");
    },
    getDaysArray() {
      let startDay = this.currentDate.startOf("month").startOf("isoWeek");
      let endDay = this.currentDate.endOf("month").endOf("isoWeek");
      let days = [];
      for (let day = startDay; day.isBefore(endDay); day = day.add(1, "day")) {
        days.push({
          date: day.format("YYYY-MM-DD"),
          number: day.format("D"),
          isCurrentMonth: day.month() === this.currentDate.month(),
          isCurrentDay:
            day.format("YYYY-MM-DD") === this.currentDate.format("YYYY-MM-DD"),
          isToday: day.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD"),
          isStartOfMonth: day.date() === 1,
          isEndOfMonth: day.date() === day.daysInMonth(),
          isTopLeftDay: day.isSame(startDay, "day"),
          isTopRightDay: day.isSame(startDay.add(6, "day"), "day"),
          isBottomLeftDay: day.isSame(endDay.subtract(6, "day"), "day"),
          isBottomRightDay: day.isSame(endDay, "day"),
        });
      }
      return days;
    },
    unauthenticatedInterviewers: [],
    // calendar data format: {id: string, events: array}
    calendars: [],
    filteredEvents(calendarData) {
      return calendarData.events.filter((event) => {
        const eventStart = this.convertToLocalTime(
          event.when.start_time,
          event.when.start_timezone,
        );
        const eventEnd = this.convertToLocalTime(
          event.when.end_time,
          event.when.end_timezone,
        );
        return (
          eventStart.isSame(this.currentDate, "day") ||
          eventEnd.isSame(this.currentDate, "day")
        );
      });
    },
    getEventClasses(event) {
      return "relative mt-px flex";
    },
    getGridRow(event) {
      const localTime = this.convertToLocalTime(
        event.when.start_time,
        event.when.start_timezone,
      );
      const hour = localTime.hour();
      const minute = localTime.minute();
      return hour * 12 + Math.floor(minute / 5) + 2; // +2 for initial offset in grid rows
    },
    getGridSpan(event) {
      const startTime = this.convertToLocalTime(
        event.when.start_time,
        event.when.start_timezone,
      );
      const endTime = this.convertToLocalTime(
        event.when.end_time,
        event.when.end_timezone,
      );
      const duration = endTime.diff(startTime, "minute");
      return Math.ceil(duration / 5); // Each grid row represents 5 minutes
    },
    formatTime(event) {
      const localTime = this.convertToLocalTime(
        event.when.start_time,
        event.when.start_timezone,
      );
      return localTime.format("h:mm A");
    },
    getDateTime(event) {
      return this.convertToLocalTime(
        event.when.start_time,
        event.when.start_timezone,
      ).toISOString();
    },
    convertToLocalTime(unixTime, timezone) {
      return dayjs.unix(unixTime).tz(timezone);
    },
    isEven(index) {
      return index % 2 === 0;
    },
    /*
     * Fetches calendar events for the given interviewer and adds them to the calendars array
     * If the interviewer is not authenticated, adds the email to the unauthenticatedInterviewers array
     *
     * @param {{ email: string, name: string, id: string }} interviewer - interviewer object
     */
    fetchCalendars(interviewer) {
      this.inProgress = true;
      fetch(
        `/scheduling/calendars/${interviewer.id}/events/${this.currentDate.startOf("day").unix()}/`,
      )
        .then(async (response) => {
          this.inProgress = false;
          if (!response.ok) {
            const data = await response.json();
            throw data;
          }
          return response.json();
        })
        .then((data) => {
          // push data to calendars array
          // check if the interviewer is already in the calendars array
          // if so, update the events array
          const index = this.calendars.findIndex(
            (calendar) => calendar.id === interviewer.email,
          );
          if (index === -1) {
            this.calendars.push({ id: interviewer.email, events: data.events });
          } else {
            this.calendars[index].events = data.events;
          }
          // Scroll to business hours after calendar data is loaded
          this.scrollToBusinessHours();
        })
        .catch((error) => {
          // If error is 400 and error.type is "grant-needed", push the email to unauthenticatedInterviewers
          if (error?.error?.type === "grant-needed") {
            this.unauthenticatedInterviewers.push(interviewer.email);
          } else {
            // Report error to sentry
            try {
              throw new Error("Unexpected Nylas error", error);
            } catch (err) {
              window.Sentry &&
                Sentry.onLoad(function () {
                  const client = Sentry.getClient();
                  client.captureException(err);
                });
            }
          }
        });
    },
    init() {
      if (this.interviewers) {
        this.calendars = [];
        this.unauthenticatedInterviewers = [];
        for (const interviewer of this.interviewers) {
          this.fetchCalendars(interviewer);
        }
      }
      this.$watch("interviewers, currentDate", (value) => {
        // For each interviewer, fetch calender events and add returned data to calendars
        // Clean up calendars and unauthenticatedInterviewers arrays before fetching new data
        this.calendars = [];
        this.unauthenticatedInterviewers = [];
        for (const interviewer of value) {
          this.fetchCalendars(interviewer);
        }
      });

      // Watch for start time changes
      this.$watch("startTime", (value) => {
        if (!value) return;

        // Parse the start time and add one hour
        const [hours, minutes] = value.split(":");
        const startDate = new Date(
          2000,
          0,
          1,
          parseInt(hours),
          parseInt(minutes),
        );
        // Special case for 11 PM (23:00)
        if (parseInt(hours) >= 23) {
          // Add 45 minutes instead of 1 hour
          this.endTime = "23:45";
        } else {
          // Normal case: add 1 hour
          const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
          this.endTime = `${endDate.getHours().toString().padStart(2, "0")}:${endDate.getMinutes().toString().padStart(2, "0")}`;
        }
      });

      // Initial scroll to business hours
      this.scrollToBusinessHours();
    },
  };
};
